import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  FileDownloadOutlined as DownloadIcon,
  UploadFileOutlined as UploadIcon,
} from '@mui/icons-material';
import type { ImportResult } from '../../types/supplier.type';
import DialogHeader from './DialogHeader';

interface BulkImportControlsProps {
  onDownloadTemplate: () => Promise<Blob>;
  templateFilename: string;
  onImport: (file: File) => Promise<ImportResult>;
  onImported?: () => void;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function BulkImportControls({
  onDownloadTemplate,
  templateFilename,
  onImport,
  onImported,
}: BulkImportControlsProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await onDownloadTemplate();
      triggerBlobDownload(blob, templateFilename);
    } catch {
      setDownloadError(t('common.bulkImport.downloadError'));
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportError(null);
    try {
      const importResult = await onImport(file);
      setResult(importResult);
      if (importResult.created > 0) onImported?.();
    } catch {
      setImportError(t('common.bulkImport.importError'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Stack direction="row" spacing={1}>
      <Button
        variant="outlined"
        size="small"
        startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
        onClick={() => void handleDownload()}
        disabled={downloading}
      >
        {t('common.bulkImport.downloadTemplate')}
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={importing ? <CircularProgress size={16} /> : <UploadIcon />}
        onClick={() => fileInputRef.current?.click()}
        disabled={importing}
      >
        {importing ? t('common.bulkImport.importing') : t('common.bulkImport.import')}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        hidden
        onChange={(event) => void handleFileSelected(event)}
      />

      {downloadError && <Alert severity="error" onClose={() => setDownloadError(null)} sx={{ position: 'fixed', bottom: 16, insetInlineEnd: 16, zIndex: 1500 }}>{downloadError}</Alert>}
      {importError && <Alert severity="error" onClose={() => setImportError(null)} sx={{ position: 'fixed', bottom: 16, insetInlineEnd: 16, zIndex: 1500 }}>{importError}</Alert>}

      <Dialog open={result != null} onClose={() => setResult(null)} fullWidth maxWidth="sm">
        <DialogHeader title={t('common.bulkImport.resultsTitle')} />
        <DialogContent>
          {result && (
            <Stack spacing={2}>
              <Alert severity={result.failed.length === 0 ? 'success' : 'warning'}>
                {t('common.bulkImport.createdCount', { created: result.created, total: result.total_rows })}
              </Alert>
              {result.failed.length > 0 && (
                <List dense disablePadding>
                  {result.failed.map((rowError) => (
                    <ListItem key={rowError.row} disableGutters alignItems="flex-start">
                      <ListItemText
                        primary={t('common.bulkImport.rowLabel', { row: rowError.row })}
                        secondary={rowError.errors.join('; ')}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResult(null)}>{t('common.bulkImport.close')}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
